import React, { useRef } from 'react'
import { useToggleState } from 'react-stately'
import { mergeProps, useToggleButton } from 'react-aria'
import type { AriaProps } from '../../../types'
import { BaseActionBarButton } from '../BaseActionBarButton'
import { splitAriaProps } from '../../../helpers'
import { Tooltip } from '../../../feedback'

export type ActionBarIconToggleButtonPublicProps = {
  ariaProps?: AriaProps
  'data-id'?: string
  'data-test-id'?: string
  icon: React.ComponentType
  isDisabled?: boolean
  label: string
  variant: 'success' | 'warning'
  INTERNAL_hasNextSibling?: boolean
  INTERNAL_isOnlyChild?: boolean
  isSelected?: boolean
  onChange?: (isSelected: boolean) => void
}

export const ActionBarIconToggleButton = (props: ActionBarIconToggleButtonPublicProps) => {
  return (
    <Tooltip
      isDisabled={props.isDisabled}
      content={props.label}
      data-test-id={props['data-test-id'] ? `${props['data-test-id']}-tooltip` : undefined}
    >
      <ActionBarIconToggleButtonInner {...props} />
    </Tooltip>
  )
}

const ActionBarIconToggleButtonInner = ({
  isDisabled,
  'data-id': dataId,
  'data-test-id': dataTestId,
  icon,
  label,
  variant,
  ariaProps,
  INTERNAL_hasNextSibling,
  INTERNAL_isOnlyChild,
  isSelected,
  onChange,
}: ActionBarIconToggleButtonPublicProps) => {
  const { elementRef, reactAriaProps, domProps } = splitAriaProps(ariaProps)
  const fallbackRef = useRef<HTMLButtonElement>(null)
  const state = useToggleState({ isDisabled, isSelected, onChange })
  const ref = elementRef ?? fallbackRef
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    buttonProps: { color: _color, ...propsWithoutColor },
  } = useToggleButton(
    {
      elementType: 'button',
      type: 'button',
      isDisabled: isDisabled,
      onChange,
      isSelected,
      'aria-label': label,
      ...reactAriaProps,
    },
    state,
    ref
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { children, ...rest } = mergeProps(propsWithoutColor, domProps)

  return (
    <BaseActionBarButton
      variant={variant}
      wrapper={{ as: 'button', ref }}
      data-id={dataId}
      data-test-id={dataTestId}
      startIcon={icon}
      INTERNAL_hasNextSibling={INTERNAL_hasNextSibling}
      INTERNAL_isOnlyChild={INTERNAL_isOnlyChild}
      {...rest}
    />
  )
}
